/* eslint-disable @typescript-eslint/naming-convention */
import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { ParamMap } from '@angular/router';
import { NextLocator, SelectOption, SelectOptionGroup } from '@grupo-constant/next-dtype/types';
import { TranslocoService } from '@ngneat/transloco';
import { cloneDeep } from 'lodash-es';
import { BehaviorSubject, map, Observable, of, tap } from 'rxjs';
import { ProjectsService } from '../projects/projects.service';

import { ServicesService } from '../services/services.service';
import {
    CancelTaskListBody,
    CancelTaskListReply,
    CompanyDataUser,
    DownloadSearchTaskBody,
    DownloadTaskModelBody,
    FilterAddress,
    FilterDate,
    FlushServiceUserAssignmentBody,
    FlushServiceUserAssignmentReply,
    FlushTaskBody,
    FlushTaskReply,
    Pagination,
    PlanningAreaAsOption,
    RemoveTaskBody,
    RemoveTaskReply,
    ResetTaskListBody,
    ResetTaskListReply,
    RetrieveTaskAssignmentsBody,
    RetrieveTaskAssignmentsReply,
    RetrieveTaskByIdBody,
    RetrieveTaskByIdReply,
    RetrieveTaskSearchOptionsBody,
    RetrieveTaskSearchOptionsReply,
    RetrieveUserCalendarBody,
    RetrieveUserCalendarReply,
    RetrieveZombieCalendarBody,
    RetrieveZombieCalendarReply,
    SearchTaskBody,
    SearchTaskReply,
    SendMessageBody,
    SendTaskListBody,
    SendTaskListReply,
    TaskSearch,
    TaskSearchOptionsFilter,
    TaskStatusEnum,
    UploadTaskModelBody,
    UploadTaskModelResponse,
    UserCalendar
} from './tasks.types';
import { WebSocketMessage } from '../../../core/websocket/websocket.types';
import { DTypeService } from '../../../core/dtype/dtype.service';
import { HttpService } from '../../../core/http/http.service';
import { LocalizePipe } from '../../../core/pipes/localize.pipe';
import { UserService } from '../../../core/user/user.service';
import { NextMethodsEnum } from '../../../core/enums/next-methods.enum';

@Injectable({ providedIn: 'root' })
export class GlobalTasksService {

    private _tasks: BehaviorSubject<TaskSearch[]> = new BehaviorSubject(undefined);
    private _task: BehaviorSubject<TaskSearch> = new BehaviorSubject(undefined);
    private _viewTasks: BehaviorSubject<TaskSearch[]> = new BehaviorSubject(undefined);
    private _pagination: BehaviorSubject<Pagination> = new BehaviorSubject(undefined);
    private _taskTypes: BehaviorSubject<SelectOptionGroup[]> = new BehaviorSubject(undefined);
    private _taskTypesForPlanning: BehaviorSubject<SelectOptionGroup[]> = new BehaviorSubject(undefined);
    private _taskType: BehaviorSubject<SelectOption> = new BehaviorSubject(undefined);
    private _planningAreas: BehaviorSubject<PlanningAreaAsOption[]> = new BehaviorSubject(undefined);
    private _planningArea: BehaviorSubject<PlanningAreaAsOption> = new BehaviorSubject(undefined);
    private _address: BehaviorSubject<FilterAddress> = new BehaviorSubject(undefined);
    private _dateFrom: BehaviorSubject<Date> = new BehaviorSubject(undefined);
    private _dateTo: BehaviorSubject<Date> = new BehaviorSubject(undefined);
    private _schedule: BehaviorSubject<FilterDate> = new BehaviorSubject(undefined);
    private _statusOptions: BehaviorSubject<SelectOption[]> = new BehaviorSubject(undefined);
    private _bodyWithFilters: BehaviorSubject<SearchTaskBody> = new BehaviorSubject(undefined);
    private statusOptionsWithoutObserver;

    private _reasonOptions: BehaviorSubject<SelectOption[]> = new BehaviorSubject(undefined);
    private _availableOptions: BehaviorSubject<TaskSearchOptionsFilter[]> = new BehaviorSubject(undefined);

    private availableUsers: BehaviorSubject<CompanyDataUser[]> = new BehaviorSubject(undefined);

    private paramKey: BehaviorSubject<string> = new BehaviorSubject(undefined);
    private paramMap: BehaviorSubject<ParamMap> = new BehaviorSubject(undefined);

    private _clientes: BehaviorSubject<SelectOption[]> = new BehaviorSubject(undefined);
    private _projects: BehaviorSubject<SelectOptionGroup[]> = new BehaviorSubject(undefined);

    private _webSocketMessage: BehaviorSubject<WebSocketMessage> = new BehaviorSubject(undefined);

    /**
     * Constructor
     */
    constructor(
        private dtypeService: DTypeService,
        private httpService: HttpService,
        private localize: LocalizePipe,
        private serviceServices: ServicesService,
        private translocoService: TranslocoService,
        private usersService: UserService,
        private projectsServices: ProjectsService
    ) { }

    get tasks$(): Observable<TaskSearch[]> {
        return this._tasks.asObservable();
    }

    /**
     * Getter for task
     */
    get task$(): Observable<TaskSearch> {
        return this._task.asObservable();
    }

    get viewTasks$(): Observable<any[]> {
        return this._viewTasks.asObservable();
    }

    get pagination$(): Observable<any> {
        return this._pagination.asObservable();
    }

    get taskTypes$(): Observable<SelectOptionGroup[]> {
        return this._taskTypes.asObservable();
    }

    get taskTypesForPlanning$(): Observable<SelectOptionGroup[]> {
        return this._taskTypesForPlanning.asObservable();
    }

    get taskType$(): Observable<SelectOption> {
        return this._taskType.asObservable();
    }

    get planningAreas$(): Observable<PlanningAreaAsOption[]> {
        return this._planningAreas.asObservable();
    }

    get planningArea$(): Observable<PlanningAreaAsOption> {
        return this._planningArea.asObservable();
    }

    get address$(): Observable<FilterAddress> {
        return this._address.asObservable();
    }

    get dateFrom$(): Observable<Date> {
        return this._dateFrom.asObservable();
    }

    get dateTo$(): Observable<Date> {
        return this._dateTo.asObservable();
    }

    get schedule$(): Observable<FilterDate> {
        return this._schedule.asObservable();
    }

    /**
     * Getter for status option list
     */
    get statusOptions$(): Observable<SelectOption[]> {
        return this._statusOptions.asObservable();
    }

    get reasonOptions$(): Observable<SelectOption[]> {
        return this._reasonOptions.asObservable();
    }

    get availableOptions$(): Observable<TaskSearchOptionsFilter[]> {
        return this._availableOptions.asObservable();
    }

    /**
     * Getter for body with filters
     */
    get bodyWithFilters$(): Observable<SearchTaskBody> {
        return this._bodyWithFilters.asObservable();
    }

    get availableUsers$(): Observable<CompanyDataUser[]> {
        return this.availableUsers.asObservable();
    }

    /**
     * Getter for params
     */

    get paramKey$(): Observable<string> {
        return this.paramKey.asObservable();
    }

    get paramMap$(): Observable<ParamMap> {
        return this.paramMap.asObservable();
    }

    /**
     * Getter for clientes y projects
     */

    get clientes$(): Observable<SelectOption[]> {
        return this._clientes.asObservable();
    }

    get projects$(): Observable<SelectOptionGroup[]> {
        return this._projects.asObservable();
    }

    /**
     * Getter for webSocket
     */
    get webSocketMessage$(): Observable<WebSocketMessage> {
        return this._webSocketMessage.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    getBrandProjectCustomer(paramMap: ParamMap) {
        if (paramMap?.keys.includes('projectId')) {
            this.setParamKey('projectId');
            return this.projectsServices.getProject(paramMap.get('projectId'));
        } else if (paramMap?.keys.includes('marca_code')) {
            if (paramMap?.keys.includes('cliente_code')) {
                this.setParamKey('cliente_code');
                return this.projectsServices.getCustomer(paramMap.get('marca_code'), Number(paramMap.get('cliente_code')));
            } else {
                this.setParamKey('marca_code');
                return this.projectsServices.getBrand(paramMap.get('marca_code'));
            }
        }
        this.setParamMap(paramMap);
    }

    searchTasks(body?: SearchTaskBody, refresh: boolean = false): Observable<TaskSearch[]> {
        const tasks = this._tasks.getValue();
        if (refresh || !tasks) {
            return this.searchTaskList(body).pipe(
                map((reply) => {
                    const newTasks = reply.task ?? [];
                    const userUri = this.serviceServices.userUri;
                    const clienteUri = this.serviceServices.DTYPE_CUSTOMER_URI;
                    const projectUri = this.serviceServices.DTYPE_PROJECT_URI;
                    const serviceUri = this.serviceServices.DTYPE_SERVICE_URI;
                    const usersPartaker = reply.partaker[userUri];
                    for (const t of newTasks) {
                        if (usersPartaker[t.who.userId]) {
                            t.who.userName = usersPartaker[t.who.userId].displayName;
                        }
                        if(reply.partaker[clienteUri]){
                            t.task.cliente_name = reply.partaker[clienteUri][t.task?.marca_code+'+'+t.task?.cliente_code].name;
                        }
                        if(reply.partaker[projectUri]){
                            t.task.project_name = reply.partaker[projectUri][t.task?.projectId].name;
                        }
                        if(reply.partaker[serviceUri]){
                            t.task.service_name = reply.partaker[serviceUri][t.task?.serviceId].name;
                        }
                    }
                    return newTasks;
                }),
                tap(newTasks => this._tasks.next(newTasks))
            );
        } else {
            return this.tasks$;
        }
    }

    getTask(taskId: string): Observable<TaskSearch> {
        const task = this._tasks.getValue()?.find(t => t.task?.taskId === taskId);
        if (task) {
            if (this._task?.getValue()?.task?.taskId !== task.task?.taskId) {
                this._task.next(task);
            }
        }
        return of(task);
    }

    setNewTask(task: TaskSearch) {
        this._task.next(task);
    }

    setTasks(tasks: TaskSearch[]) {
        this._tasks.next(tasks);
    }

    getViewTasks(body: SearchTaskBody, refresh: boolean = false, page: number = 0, size: number = 10,
        field: string = 'timestamp', dir: SortDirection = 'desc'): Observable<TaskSearch[]> {
        return this.searchTasks(body, refresh).pipe(
            map((tasks) => {
                // Clone thetasks to prevent accidental updates
                let taskList = cloneDeep(this.sortTasks(tasks, field, dir));
                const tasksLength = taskList.length;

                // Paginate - Start

                // Calculate pagination details
                const begin = page * size;
                const end = Math.min((size * (page + 1)), tasksLength);
                const lastPage = Math.max(Math.ceil(tasksLength / size), 1);

                // Prepare the pagination object
                let pagination: Partial<Pagination>;

                // If the requested page number is bigger than
                // the last possible page number, return null for
                // tasks but also send the last possible page so
                // the app can navigate to there
                if (page > lastPage) {
                    pagination = {
                        lastPage
                    };
                } else {
                    // Paginate the results by 10
                    taskList = taskList.slice(begin, end);

                    // Prepare the pagination mock-api
                    pagination = {
                        length: tasksLength,
                        size,
                        page,
                        lastPage: lastPage,
                        startIndex: begin,
                        endIndex: end - 1
                    };
                }

                this._viewTasks.next(taskList);
                this._pagination.next(pagination as Pagination);
                return taskList;
            })
        );
    }

    updateTask(body: FlushTaskBody): Observable<FlushTaskReply> {
        return this.flushTask(body);
    }

    getTaskAssignments(serviceId: string, planningArea: string) {
        return this.retrieveTaskAssignments({ serviceId: serviceId }).pipe(
            map(reply => reply[planningArea])
        );
    }

    getUserCalendar(userId: string, date: string): Observable<UserCalendar[]> {
        return this.retrieveUserCalendar({ userId: userId, date: date }).pipe(
            map(reply => reply.calendar)
        );
    }
    getZombieCalendar(serviceId: string, planningArea: string, date: string, zombieNumber: number): Observable<UserCalendar[]> {
        return this.retrieveZombieCalendar({ serviceId: serviceId, planningArea: planningArea, date: date, zombieNumber: zombieNumber }).pipe(
            map(reply => reply.calendar)
        );
    }

    setTaskType(taskType: SelectOption) {
        this._taskType.next(taskType);
    }

    setPlanningArea(planningArea: PlanningAreaAsOption) {
        this._planningArea.next(planningArea);
    }

    setDateFrom(date: Date) {
        this._dateFrom.next(date);
    }

    setDateTo(date: Date) {
        this._dateTo.next(date);
    }

    setBodyWithFilters(body: SearchTaskBody) {
        this._bodyWithFilters.next(body);
    }

    setAvailableUsers(users: CompanyDataUser[]) {
        this.availableUsers.next(users);
    }

    setViewTasks(taskList: TaskSearch[]) {
        this._viewTasks.next(taskList);
    }

    setParamKey(paramKey: string) {
        this.paramKey.next(paramKey);
    }

    setParamMap(paramMap: ParamMap) {
        this.paramMap.next(paramMap);
    }

    setWebSocketMessage(message: WebSocketMessage) {
        this._webSocketMessage.next(message);
    }

    searchAddress(expr: string, uri: string) {
        return this.dtypeService.searchDTypeDataCollection({
            expr,
            uri
        });
    }

    searchName(serviceId: string, expr: string, planningArea: string) {
        const locator: NextLocator = {
            serviceId
        };
        return this.usersService.searchCompanyUserDN(
            locator,
            expr,
            planningArea
        );
    }

    searchMissionUserListByBrand(marca_code: string, activeOnly: boolean = false, expr: string) {
        return this.usersService.searchMissionUserListByBrand(
            expr,
            activeOnly,
            marca_code
        );
    }

    getTaskSearchOptions(body: RetrieveTaskSearchOptionsBody) {
        return this.httpService.post<RetrieveTaskSearchOptionsReply>(NextMethodsEnum.retrieveTaskSearchOptions, body).pipe(
            tap((reply) => {
                this._availableOptions.next(reply.availableSearchOptions);
                this._planningAreas.next(reply.planningArea);
                this._taskTypes.next(reply.taskType);
                this._taskTypesForPlanning.next(reply.taskTypeForPlanning);
                this._projects.next(reply.project);
                this._clientes.next(reply.customer);
                this._schedule.next(reply.schedule);

                reply.statuses.splice(reply.statuses.findIndex(s => s.code === 'REJECTED'), 1);
                this._statusOptions.next(reply.statuses);

                this.statusOptionsWithoutObserver = reply.statuses;
                this._reasonOptions.next(reply.reason);
                this._address.next(reply.address);
                return reply;
            })
        );
    }

    getSavedAddressAndEventSummary(body: RetrieveTaskByIdBody): Observable<RetrieveTaskByIdReply> {
        return this.retrieveTaskById(body);
    }

    sendTaskList(body: SendTaskListBody) {
        return this.httpService.post<SendTaskListReply>(NextMethodsEnum.sendTaskList, body);
    }

    cancelTaskList(body: CancelTaskListBody) {
        return this.httpService.post<CancelTaskListReply>(NextMethodsEnum.cancelTaskList, body);
    }
    resetTaskList(body: ResetTaskListBody) {
        return this.httpService.post<ResetTaskListReply>(NextMethodsEnum.resetTaskList, body);
    }

    removeTask(body: RemoveTaskBody) {
        return this.httpService.post<RemoveTaskReply>(NextMethodsEnum.deleteTask, body);
    }

    flushServiceUserAssignment(body: FlushServiceUserAssignmentBody) {
        return this.httpService.post<FlushServiceUserAssignmentReply>(NextMethodsEnum.flushServiceUserAssignment, body);
    }

    downloadSearchTask(body: DownloadSearchTaskBody): any {
        return this.httpService.postWithHeaders<any>(NextMethodsEnum.downloadSearchTaskXSSF, body, 'blob');
    }

    downloadTaskModel(body: DownloadTaskModelBody): any {
        return this.httpService.postWithHeaders<any>(NextMethodsEnum.downloadServiceTasksModelXSSF, body, 'blob');
    }

    uploadTaskModel(body: UploadTaskModelBody): Observable<UploadTaskModelResponse> {
        return this.httpService.post<UploadTaskModelResponse>(NextMethodsEnum.uploadServiceTasksModelXSSF, body);
    }

    sendMessage(body: SendMessageBody) {
        return this.httpService.post<any>(NextMethodsEnum.sendNotificationForTask, body);
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Private methods
    // -----------------------------------------------------------------------------------------------------
    private searchTaskList(body: SearchTaskBody) {
        return this.httpService.post<SearchTaskReply>(NextMethodsEnum.searchTask, body);
    }

    private flushTask(body: FlushTaskBody) {
        return this.httpService.post<FlushTaskReply>(NextMethodsEnum.flushTask, body);
    }

    private compareStrings(string1: string, string2: string, dir: SortDirection) {
        if (string1 === '') {
            if (string2 === '') {
                return 0;
            } else {
                return dir === 'asc' ? 0 : -1;
            }
        } else if (string2 === '') {
            if (string1 === '') {
                return 0;
            } else {
                return dir === 'asc' ? -1 : 0;
            }
        } else {
            return dir === 'asc' ? string1.localeCompare(string2) : string2.localeCompare(string1);
        }
    }
    private getStatusLabel(statusCode: TaskStatusEnum) {
        const option = this.statusOptionsWithoutObserver.find(so => so.code === statusCode);
        return option?.value;
    }

    private sortTasks(tasks: TaskSearch[], field: string, dir: SortDirection) {
        return tasks.sort((a, b) => {
            switch (field) {
                case 'info':
                    if (!a.alert) {
                        if (!b.alert) {
                            return 0;
                        } else {
                            return dir === 'asc' ? 0 : -1;
                        }
                    } else if (!b.alert) {
                        if (!a.alert) {
                            return 0;
                        } else {
                            return dir === 'asc' ? -1 : 0;
                        }
                    } else {
                        return this.compareStrings(a.alert[0].type ?? '', b.alert[0].type ?? '', dir);
                    }
                case 'taskId':
                    return this.compareStrings(a.task.taskId ?? '', b.task.taskId ?? '', dir);
                case 'zombieName':
                    return this.compareStrings(a.who.zombieName ?? '', b.who.zombieName ?? '', dir);
                case 'userName':
                    return this.compareStrings(a.who.userName ?? '', b.who.userName ?? '', dir);
                case 'planningAreaName':
                    return this.compareStrings(a.group.planningAreaName ?? '', b.group.planningAreaName ?? '', dir);
                case 'taskTypeName':
                    return this.compareStrings(a.task.taskTypeName ?? '', b.task.taskTypeName ?? '', dir);
                case 'customer':
                    return this.compareStrings(a.task.cliente_name ?? '', b.task.cliente_name ?? '', dir);
                case 'project':
                    return this.compareStrings(a.task.project_name ?? '', b.task.project_name ?? '', dir);
                case 'service':
                    return this.compareStrings(a.task.service_name ?? '', b.task.service_name ?? '', dir);
                case 'scheduled_from_date':
                    return this.compareStrings(a.schedule.timeFrom ?? '', b.schedule.timeFrom ?? '', dir);
                case 'addressName':
                    return this.compareStrings(a.where.addressName ?? '', b.where.addressName ?? '', dir);
                case 'fullAddress':
                    return this.compareStrings(a.where.addressVicinity ?? '', b.where.addressVicinity ?? '', dir);
                case 'timestamp':
                    return this.compareStrings(a.status.timestamp ?? '', b.status.timestamp ?? '', dir);
                case 'status':
                    const stsA = this.getStatusLabel(a.status.status);
                    const stsB = this.getStatusLabel(b.status.status);
                    return (this.compareStrings((this.localize.transform(stsA)) ?? '', (this.localize.transform(stsB)) ?? '', dir) === 0 ?
                        this.compareStrings((a.status.timestamp) ?? '', b.status.timestamp ?? '', dir) : this.compareStrings(this.translocoService.translate(a.status.status) ?? '',
                            this.translocoService.translate(a.status.status) ?? '', dir));
            }
            return 0;
        });
    }

    private retrieveTaskAssignments(body: RetrieveTaskAssignmentsBody) {
        return this.httpService.post<RetrieveTaskAssignmentsReply>(NextMethodsEnum.retrieveTaskAssignments, body);
    }

    private retrieveUserCalendar(body: RetrieveUserCalendarBody) {
        return this.httpService.post<RetrieveUserCalendarReply>(NextMethodsEnum.retrieveUserCalendar, body);
    }

    private retrieveZombieCalendar(body: RetrieveZombieCalendarBody) {
        return this.httpService.post<RetrieveZombieCalendarReply>(NextMethodsEnum.retrieveZombieCalendar, body);
    }

    private retrieveTaskById(body: RetrieveTaskByIdBody) {
        return this.httpService.post<RetrieveTaskByIdReply>(NextMethodsEnum.retrieveTaskById, body);
    }

}
